import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { showToast } from '@/helpers/toast-handler';
import { getValidationMessageByRule } from '../utils/validations-messages';

import useUserComposable from '@/composables/UserComposable';
import GlobalRequest from '@/api/global-request';

const ASSESSMENT_FORM_URL = 'v2/assessment/form';
const USER_PROFILE_URL = 'v2/user_profile';
const { DateTime } = require('luxon');

export const useAssessmentFormStore = defineStore('assessmentForm', () => {
    const { company_id } = useUserComposable();
    // states
    const loading = ref(null);
    // current assessment submission date, by default its set to today
    const assessment_date = ref(new Date());
    // current step answers (postable format extracted from backapi answerData key)
    const currentStepAnswer = ref({});
    // array of currentStepAnswers that have already been submitted (as draft=true mode)
    const answers = ref([]);
    // data extracted from nextStep key from api that holds block and its attribute related information
    const currentStep = ref({});
    // array of currentSteps in the order of being submitted
    // the steps array can change continously while going next and previous and
    // in case user submits a different currentStepAnswer after going previous, it can lead to changed steps array
    const steps = ref([]);
    // flag for tracking final submission
    const finalSubmission = ref(false);
    // array to hold invalid attributes and validation errors that occured during form submission respectively
    const invalidAttributeIds = ref([]);
    const validationErrors = ref([]);
    // helpers

    // this function will format and map the formData values that contains the users entered information into currentStepAnswer->answers
    function formatCurrentStepAnswers(formData) {
        // set the selected date
        currentStepAnswer.value.assessment_date = assessment_date.value;
        // loop through the answers from currentStepAnswer
        currentStepAnswer.value.answers = currentStepAnswer.value.answers.map((item) => {
            // map the answers accordingly with the a_ attribute_id
            return {
                ...item,
                // if formData['a_key'] doesnt have any data then put them as '' (empty string)
                answer: formData['a_' + item.attribute_id] ?? null,
            };
        });
    }

    function handleNextStep(response, submittedData) {
        // CASE 1:- Submitted data could already be an existing step which the user could have been re-submitting again
        // so, check if step has already been submitted
        // to do so, check for the index of submittedData step_code the steps array
        const existingStepIndex = steps.value?.findIndex((step) => step.code === submittedData.current_step_code);
        // if the submitted step already existing in the existing steps array state then
        if (existingStepIndex !== -1) {
            // CASE 1.1:- There are more existing steps and possibly a next step of an existing step already
            // check if there is next step data as well and get it from steps
            const existingNextStep = steps.value[existingStepIndex + 1];
            // if there is an existingNextStep in the steps
            if (existingNextStep) {
                // CASE 1.1.1:- There is an existing next step as well and its exactly the same as the current response
                // compare the existingNextStep code with the nextStep code got from the response
                //  if its the same the extract the existing nextStep related information from it and swap into the currentStep and currentStepAnswer
                if (existingNextStep?.code === response.nextStep?.code) {
                    // if it matches then put the currentStep and currentStepAnswer as it is
                    currentStep.value = existingNextStep;
                    // get the existing answer data from answers
                    currentStepAnswer.value = answers.value[existingStepIndex + 1];
                }
                // if existingNextStep code doesnt match the nextStepCode
                //
                else {
                    // delete the next indexes for both answers and steps
                    steps.value?.splice(existingStepIndex + 1);
                    answers.value?.splice(existingStepIndex + 1);
                    //  update the answers into answers array
                    answers.value[existingStepIndex] = submittedData;
                    // now after that swap the currentStep with response.data -> nextStep
                    currentStep.value = response.nextStep;
                    //  and currentStepAnswer with response.data -> answerData
                    currentStepAnswer.value = response.answerData;
                }
            }
            // if its just a previous step and there is no next step yet, just update the existing step answers
            // and update the currentStep and currentStepAnswers
            else {
                currentStep.value = response.nextStep;
                // after the first step has been submitted, we receive a form_id which needs to be updated from next step onwards.
                // This will exist in multi steps assessments, however with some assessments with single step, this will not exist, so to avoid issues of creating new form id
                currentStepAnswer.value = { ...response.answerData, form_id: response.answerData?.form_id };
                // now make sure to update the existing stepIndex of the answers with the submitted data
                answers[existingStepIndex] = submittedData;
            }
        }
        // otherwise just push the data into the array normally
        else {
            //  add currentStep into steps array
            steps.value.push(currentStep.value);
            //  push the currentStepAnswer into answers array
            answers.value.push(submittedData);
            // now after that swap the currentStep with response.data -> nextStep
            currentStep.value = response.nextStep;
            //  and currentStepAnswer with response.data -> answerData
            // This will exist in multi steps assessments, however with some assessments with single step, this will not exist, so to avoid issues of creating new form id
            currentStepAnswer.value = { ...response.answerData, form_id: response.answerData?.form_id };
        }
    }

    // this helper will reset the assessmentForm states to it initial state
    function resetAssessmentForm() {
        currentStepAnswer.value = {};
        answers.value = [];
        currentStep.value = {};
        steps.value = [];
        finalSubmission.value = false;
        validationErrors.value = [];
    }

    // getters
    // returns invalid input error messages on basis of given attribute id
    const getValidationErrorMessage = (id) => {
        const invalidAttribute = validationErrors.value?.find((error) => error.attribute_id === id);
        if (invalidAttribute) {
            return invalidAttribute.message;
        }
    };

    // check if current step has previous step or not
    const hasPreviousStep = computed(() => {
        // CASE 1: currentStep is the first step and steps array is empty
        if (steps.value.length <= 0) {
            return false;
        }
        // CASE 2: currentStep is a new step and it doesnt exist in the array yet
        // To test it out first check for the existing index of the current step and if it doesnt exist then
        const existingIndex = steps.value.findIndex((step) => step.code === currentStep.value?.code);
        if (existingIndex == -1) {
            return true;
        }
        // if it exists then it has to be atleast greater than 0
        else if (existingIndex > 0) {
            return true;
        }
        return false;
    });

    // actions

    // this function generates the starting step of an assessment
    // once you start submitting the assessment, the nextStep is given in every response
    // you will use this function only in the starting point
    const initiateAnswerForm = async (assessmentCode) => {
        try {
            loading.value = true;
            const URL = assessmentCode === 'user_profile' ? USER_PROFILE_URL : `${ASSESSMENT_FORM_URL}/${assessmentCode}`;
            const response = await GlobalRequest.getRequest(URL, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                // current step details
                currentStep.value = response.data.nextStep;
                // this is where you fill your answers and submit them
                currentStepAnswer.value = response.data.answerData;
            }
            return response.data;
        } catch (error) {
            showToast({}, error.data?.message ?? 'Error generating answer form');
        } finally {
            loading.value = false;
        }
    };

    async function submitCurrentStep(submitData) {

        //console.log('submitCurrentStep  ', submitData)

        try {

            loading.value = true;
            invalidAttributeIds.value = [];
            validationErrors.value = [];
            const isUserProfile = submitData?.current_assessment_code === 'user_profile';
            const URL = isUserProfile ? USER_PROFILE_URL : ASSESSMENT_FORM_URL;
            // check if currentStep is the final step, (In case its a user profile attribute as well then directly post it)
            // to do it you can check the submitData which contains the value of the currentStep
            // the currentStep will be null if there is no any data for submission
            // so, if currentStep is null then change the draft value to false as a final submission of submitData
            if (!currentStep.value) {
                // before submitting, make sure the submitData has the values from latest answers array
                submitData = answers.value[answers.value.length - 1];
                // set the draft to false before submission
                submitData.draft = false;
                // if there is a form id then append it
                submitData.form_id = currentStepAnswer.value.form_id;
                finalSubmission.value = true;
            }
            if (isUserProfile) {
                // set the draft to false before submission
                submitData.draft = false;
            }

            const assessmentDate = DateTime.fromJSDate(assessment_date.value).setLocale('en-GB').toFormat('yyyy-MM-dd')
            submitData.assessment_date = assessmentDate

            const response = await GlobalRequest.postRequest(`${URL}?company_id=${company_id.value}`, submitData);
            // after current step is successfully submitted then
            if (response.statusCode == 200) {
                // in case its a user profile assessment then make it as a final submission
                if (isUserProfile) {
                    finalSubmission.value = true;
                }
                // Check if its a final submission
                if (finalSubmission.value) {
                    showToast(response, 'Assessment submitted successfully');
                } else {
                    handleNextStep(response.data, submitData);
                }
            }
        } catch (error) {
            finalSubmission.value = false;
            // check if validation errors occurred
            const errors = error?.data?.data;
            // If its validation related errors then show the validation issues
            if (errors) {
                errors?.forEach((error) => {
                    if (error && error?.errorCode && error?.errorDesc) {
                        // if yes then check for the validation error and display it
                        // extract what validation error occured and on which attribute
                        const ruleIssue = {
                            rule: error?.validationRule?.code,
                            attributeId: error?.data?.attribute_id,
                            expectedValue: error?.rule?.value,
                        };
                        // show error of validation in a toast
                        const validationMessage = getValidationMessageByRule(ruleIssue.rule, ruleIssue.expectedValue);
                        // set the attribute id with the validation issue to highlight it
                        invalidAttributeIds.value.push(ruleIssue.attributeId);
                        // push all validation error messages and invalid attribute id
                        validationErrors.value.push({
                            attribute_id: ruleIssue.attributeId,
                            message: validationMessage ?? 'Field value is required'
                        });
                    }
                });
                showToast({}, error.data?.message ?? 'Error submitting answer form');
            }
            // Otherwise throw an error
            else {
                throw error;
            }
        } finally {
            loading.value = false;
        }
    }

    // switch steps
    const goPreviousStep = () => {
        // first check currentStep index and then move back accordingly
        // CASE 1: You are traversing back to a previous step from a step which has already been submitted and exists in the array (POV: you are hitting previous multiple times)
        // For this check if currentStep already exist in the steps array
        const existingIndex = steps.value?.findIndex((step) => step.code === currentStep.value?.code);
        // If it does exist already then traverse back one index back
        if (existingIndex !== -1) {
            // make sure the index value of existingIndex is greater than 0
            // In the UI the previous step wont be shown for currentStep with index 0 so we dont have to check it here
            const previousStepCode = steps.value[existingIndex - 1]?.code;
            // once you get the code you can easily switch the step using the existing switchStep action
            switchStep(previousStepCode);
        }
        // CASE 2: You are in a new unsubmitted step and trying to go back to a previous step
        else {
            // If you are in a new unsubmitted page then
            // TEST CASE HERE:- You may want to warn user for unsubmitted information being lost as well
            // ELSE: Just get the code from last index of steps and switch to it
            const lastStepCode = steps.value[steps.value.length - 1]?.code;
            switchStep(lastStepCode);
        }
    };

    const switchStep = (stepCode) => {
        currentStep.value = steps.value?.find((step) => step.code === stepCode);
        currentStepAnswer.value = answers.value?.find((answer) => answer.current_step_code === stepCode);
    };

    return {
        // states
        loading,
        assessment_date,
        currentStepAnswer,
        answers,
        currentStep,
        steps,
        finalSubmission,
        invalidAttributeIds,
        validationErrors,
        // helpers,
        formatCurrentStepAnswers,
        resetAssessmentForm,
        getValidationErrorMessage,
        // getters
        hasPreviousStep,
        // actions
        initiateAnswerForm,
        submitCurrentStep,
        goPreviousStep,
        switchStep,
    };
});
